import { useState, useMemo } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useData } from "@/hooks/useData";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DistributionProductStep } from "./distribution/DistributionProductStep";
import { DistributionCarbonFootprintStep } from "./distribution/DistributionCarbonFootprintStep";
import { DistributionHydricFootprintStep } from "./distribution/DistributionHydricFootprintStep";
import { DistributionCarbonPieChart } from "./DistributionCarbonPieChart";
import { DistributionHydricPieChart } from "./DistributionHydricPieChart";
import { useDistributionData } from "@/hooks/distribution/useDistributionData";
import { useToast } from "@/components/ui/use-toast";
import { Truck, Plus, Info, Edit, Trash2, Zap, Droplets } from "lucide-react";
import { FootprintProduct } from "@/types/results";
import { DistributionData } from "@/types/distribution/distribution.types";
import { TransportDistribution } from "@/types/transport-distribution/transport-distribution.types";

interface DistributionAnalysisSectionProps {
  selectedProduct?: string;
  selectedYear?: string;
}

export const DistributionAnalysisSection = ({}: DistributionAnalysisSectionProps) => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const { distributionData, createDistribution, updateDistribution, deleteDistribution } = useDistributionData();
  
  // Fetch footprint products for editing
  const { data: footprintProducts } = useData<FootprintProduct>({
    collectionName: "footprint_products",
  });

  // Fetch transport distributions for calculations
  const { data: transportDistributions } = useData<TransportDistribution>({
    collectionName: "setup_transportDistributions",
  });
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedProduct, setSelectedProduct] = useState<FootprintProduct | null>(null);
  const [selectedFootprintType, setSelectedFootprintType] = useState<'carbon' | 'hydric' | null>(null);
  const [transportData, setTransportData] = useState<any[]>([]);
  const [editingDistribution, setEditingDistribution] = useState<DistributionData | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  // State for distribution chart selection
  const [selectedDistributionProduct, setSelectedDistributionProduct] = useState<string>("");
  const [selectedDistributionFootprintType, setSelectedDistributionFootprintType] = useState<'carbon' | 'hydric' | "">("");

  // Get products with distribution data
  const availableProducts = useMemo(() => {
    const productIds = Array.from(new Set(distributionData.map(d => d.product_id)));
    return productIds.map(productId => {
      const product = footprintProducts?.find(p => p.id === productId);
      const carbonDistribution = distributionData.find(d => d.product_id === productId && d.footprint_type === 'carbon');
      const hydricDistribution = distributionData.find(d => d.product_id === productId && d.footprint_type === 'hydric');
      
      return {
        id: productId,
        name: product?.name || `Product ${productId}`,
        hasCarbon: !!carbonDistribution,
        hasHydric: !!hydricDistribution,
        product: product
      };
    }).filter(p => p.product);
  }, [distributionData, footprintProducts]);

  // Get footprint types for selected distribution product
  const availableFootprintTypes = useMemo(() => {
    if (!selectedDistributionProduct) return [];
    
    const productData = availableProducts.find(p => p.id === selectedDistributionProduct);
    if (!productData) return [];

    const types: Array<{ value: 'carbon' | 'hydric', label: string, icon: React.ReactNode }> = [];
    
    if (productData.hasCarbon) {
      types.push({
        value: 'carbon',
        label: t("results.tabs.carbon"),
        icon: <Zap className="h-4 w-4" />
      });
    }
    
    if (productData.hasHydric) {
      types.push({
        value: 'hydric',
        label: t("results.tabs.hydrics"),
        icon: <Droplets className="h-4 w-4" />
      });
    }
    
    return types;
  }, [selectedDistributionProduct, availableProducts, t]);

  // Get selected product data for charts
  const selectedProductForChart = useMemo(() => {
    if (!selectedDistributionProduct) return null;
    return footprintProducts?.find(p => p.id === selectedDistributionProduct) || null;
  }, [selectedDistributionProduct, footprintProducts]);

  // Sort distribution data alphabetically by product name
  const sortedDistributionData = useMemo(() => {
    return [...distributionData].sort((a, b) => {
      const productA = footprintProducts?.find(p => p.id === a.product_id)?.name || "";
      const productB = footprintProducts?.find(p => p.id === b.product_id)?.name || "";
      return productA.localeCompare(productB);
    });
  }, [distributionData, footprintProducts]);

  // Reset footprint type when product changes
  const handleDistributionProductChange = (productId: string) => {
    setSelectedDistributionProduct(productId);
    setSelectedDistributionFootprintType("");
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
    setCurrentStep(1);
    setSelectedProduct(null);
    setSelectedFootprintType(null);
    setTransportData([]);
    setEditingDistribution(null);
    setIsEditing(false);
  };

  const handleEditDistribution = async (distribution: DistributionData) => {
    setEditingDistribution(distribution);
    setIsEditing(true);
    setIsModalOpen(true);
    setCurrentStep(2);
    setSelectedFootprintType(distribution.footprint_type);
    
    // Fetch actual product details from footprint_products collection
    const product = footprintProducts?.find(p => p.id === distribution.product_id);
    
    if (product) {
      setSelectedProduct(product);
    } else {
      // If product not found, show error and don't proceed with edit
      console.error(`Product with ID ${distribution.product_id} not found in footprint_products`);
      toast({
        title: t("error"),
        description: t("results.distribution.errors.productNotFound"),
        variant: "destructive",
      });
      return;
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentStep(1);
    setSelectedProduct(null);
    setSelectedFootprintType(null);
    setTransportData([]);
    setEditingDistribution(null);
    setIsEditing(false);
  };

  const handleStep1Next = (product: FootprintProduct, footprintType: 'carbon' | 'hydric') => {
    setSelectedProduct(product);
    setSelectedFootprintType(footprintType);
    setCurrentStep(2);
    console.log('Step 1 completed:', { product, footprintType });
  };

  const handleStep2Next = async (data: any[]) => {
    setTransportData(data);
    
    try {
      if (!transportDistributions || !selectedProduct) {
        console.error('Missing required data for calculations');
        toast({
          title: t("error"),
          description: t("results.distribution.errors.missingData"),
          variant: "destructive",
        });
        return;
      }

      if (isEditing && editingDistribution) {
        // Update existing distribution
        const success = await updateDistribution(
          editingDistribution.id, 
          { transport_data: data },
          transportDistributions,
          selectedProduct,
          "transformation",
          editingDistribution.footprint_type
        );
        
        if (success) {
          handleCloseModal();
        }
      } else if (selectedProduct) {
        // Create new distribution
        const distributionId = await createDistribution(
          {
            product_id: selectedProduct.id,
            footprint_id: selectedProduct.footprint_id,
            footprint_type: selectedFootprintType!,
            transport_data: data,
          },
          transportDistributions,
          selectedProduct,
          "transformation"
        );
        
        if (distributionId) {
          handleCloseModal();
        }
      }
    } catch (error) {
      console.error('Error saving distribution data:', error);
      toast({
        title: t("error"),
        description: t("results.distribution.errors.saveFailed"),
        variant: "destructive",
      });
    }
  };

  const handleStep2Back = () => {
    setCurrentStep(1);
  };

  const handleDeleteDistribution = async (distributionId: string) => {
    try {
      const distribution = distributionData.find(d => d.id === distributionId);
      const success = await deleteDistribution(distributionId, distribution?.product_id);
      
      if (!success) {
        toast({
          title: t("error"),
          description: t("results.distribution.errors.deleteFailed"),
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error deleting distribution:', error);
      toast({
        title: t("error"),
        description: t("results.distribution.errors.deleteFailed"),
        variant: "destructive",
      });
    }
  };

  // Get legend content based on activity type and footprint type
  const getLegendContent = () => {
    if (!selectedProductForChart) return null;
    
    const activityType = selectedProductForChart.product_flow || "extraction";
    const footprintType = selectedDistributionFootprintType;

    if (footprintType === 'carbon') {
      if (activityType === "extraction") {
        return (
          <div className="space-y-3">
            <p className="text-sm text-gray-700 font-medium">
              {t("results.distribution.carbon.legend.extraction.description")}
            </p>
            <div className="space-y-2 text-sm text-gray-600">
              <p>• {t("results.distribution.carbon.legend.extraction.extraction")}</p>
              <p>• {t("results.distribution.carbon.legend.extraction.distribution")}</p>
            </div>
          </div>
        );
      } else if (activityType === "transformation") {
        return (
          <div className="space-y-3">
            <p className="text-sm text-gray-700 font-medium">
              {t("results.distribution.carbon.legend.transformation.description")}
            </p>
            <div className="space-y-2 text-sm text-gray-600">
              <p>• {t("results.distribution.carbon.legend.transformation.extraction")}</p>
              <p>• {t("results.distribution.carbon.legend.transformation.transport")}</p>
              <p>• {t("results.distribution.carbon.legend.transformation.transformation")}</p>
              <p>• {t("results.distribution.carbon.legend.transformation.distribution")}</p>
            </div>
          </div>
        );
      }
    } else if (footprintType === 'hydric') {
      if (activityType === "extraction") {
        return (
          <div className="space-y-3">
            <p className="text-sm text-gray-700 font-medium">
              {t("results.distribution.hydric.legend.extraction.description")}
            </p>
            <div className="space-y-2 text-sm text-gray-600">
              <p>• {t("results.distribution.hydric.legend.extraction.extraction")}</p>
              <p>• {t("results.distribution.hydric.legend.extraction.distribution")}</p>
            </div>
          </div>
        );
      } else if (activityType === "transformation") {
        return (
          <div className="space-y-3">
            <p className="text-sm text-gray-700 font-medium">
              {t("results.distribution.hydric.legend.transformation.description")}
            </p>
            <div className="space-y-2 text-sm text-gray-600">
              <p>• {t("results.distribution.hydric.legend.transformation.extraction")}</p>
              <p>• {t("results.distribution.hydric.legend.transformation.transport")}</p>
              <p>• {t("results.distribution.hydric.legend.transformation.transformation")}</p>
              <p>• {t("results.distribution.hydric.legend.transformation.distribution")}</p>
            </div>
          </div>
        );
      }
    }

    return null;
  };

  return (
    <>
      <Card className="bg-gradient-to-r from-teal-50 to-cyan-50 border-teal-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-teal-800">
            <Truck className="h-5 w-5" />
            {t("results.distribution.title")}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-4 w-4 text-teal-600 cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p>{t("results.distribution.tooltip")}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </CardTitle>
          <p className="text-sm text-teal-600">
            {t("results.distribution.description")}
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {distributionData.length > 0 ? (
            // When distribution data exists - show data visualization
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">
                  {t("results.distribution.existingData.title")}
                </h3>
                <Button
                  onClick={handleOpenModal}
                  className="bg-teal-600 hover:bg-teal-700 text-white"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t("results.distribution.button.addDistribution")}
                </Button>
              </div>
              
              {/* Distribution Data List */}
              <div className="space-y-3">
                {sortedDistributionData.map((distribution) => (
                  <div key={distribution.id} className="p-4 border border-gray-200 rounded-lg bg-white hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <div className={`flex items-center gap-1 text-xs px-2 py-1 rounded ${
                            distribution.footprint_type === 'carbon' 
                              ? 'bg-blue-50' 
                              : 'bg-green-50'
                          }`}>
                            {distribution.footprint_type === 'carbon' ? (
                              <Zap className="h-3 w-3 text-blue-600" />
                            ) : (
                              <Droplets className="h-3 w-3 text-green-600" />
                            )}
                            <span className={`font-medium ${
                              distribution.footprint_type === 'carbon' 
                                ? 'text-blue-700' 
                                : 'text-green-700'
                            }`}>
                              {distribution.footprint_type === 'carbon' ? 'CO₂' : 'H₂O'}
                            </span>
                          </div>
                        </div>
                        <div className="text-sm text-gray-600">
                          <p><strong>{t("results.distribution.existingData.product")}:</strong> {
                            footprintProducts?.find(p => p.id === distribution.product_id)?.name || 
                            `Product ${distribution.product_id}`
                          }</p>
                          <p><strong>{t("results.distribution.existingData.transports")}:</strong> {distribution.transport_data.length}</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditDistribution(distribution)}
                          className="text-teal-600 hover:text-teal-700 hover:bg-teal-50"
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          {t("results.distribution.edit")}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteDistribution(distribution.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          {t("results.distribution.delete")}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Distribution Pie Charts */}
              <div className="mt-8">
                <h4 className="text-lg font-medium text-gray-900 mb-4">
                  {t("results.distribution.charts.title")}
                </h4>
                
                {/* Product and Footprint Type Selection */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">{t("results.distribution.filters.product")}</label>
                    <Select value={selectedDistributionProduct} onValueChange={handleDistributionProductChange}>
                      <SelectTrigger>
                        <SelectValue placeholder={t("results.distribution.filters.selectProduct")} />
                      </SelectTrigger>
                      <SelectContent>
                        {availableProducts.sort((a, b) => a.product?.name.localeCompare(b.product?.name || "") || 0).map((product) => (
                          <SelectItem key={product.id} value={product.id}>
                            {product.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">{t("results.distribution.filters.footprintType")}</label>
                    <Select value={selectedDistributionFootprintType} onValueChange={(value) => setSelectedDistributionFootprintType(value as 'carbon' | 'hydric' | '')}>
                      <SelectTrigger>
                        <SelectValue placeholder={t("results.distribution.filters.selectFootprintType")} />
                      </SelectTrigger>
                      <SelectContent>
                        {availableFootprintTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            <div className="flex items-center gap-2">
                              {type.icon}
                              {type.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Chart Display */}
                {selectedDistributionProduct && selectedDistributionFootprintType && selectedProductForChart ? (
                  <div className="space-y-6">
                    {/* Chart Section */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Chart */}
                      <div>
                        {selectedDistributionFootprintType === 'carbon' ? (
                          <DistributionCarbonPieChart product={selectedProductForChart} />
                        ) : selectedDistributionFootprintType === 'hydric' ? (
                          <DistributionHydricPieChart product={selectedProductForChart} />
                        ) : null}
                      </div>
                      
                      {/* Legend Content */}
                      <div className="flex items-center">
                        <Card className="w-full h-full">
                          <CardHeader className="pb-3">
                            <CardTitle className="flex items-center gap-2">
                              <Info className="h-5 w-5" />
                              {selectedDistributionFootprintType === 'carbon' 
                                ? t("results.distribution.carbon.legend.title")
                                : t("results.distribution.hydric.legend.title")
                              }
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            {getLegendContent()}
                          </CardContent>
                        </Card>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <Truck className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>
                      {t("results.distribution.charts.selectProductAndType")}
                    </p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            // Empty state when no distribution data
            <div className="text-center py-8">
              <Truck className="h-16 w-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {t("results.distribution.empty.title")}
              </h3>
              <p className="text-gray-500 mb-6 max-w-md mx-auto">
                {t("results.distribution.empty.description")}
              </p>
              
              {/* Get Started Steps */}
              <div className="text-left max-w-md mx-auto mb-6">
                <h4 className="text-sm font-medium text-gray-700 mb-3">
                  {t("results.distribution.empty.getStarted")}
                </h4>
                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-start gap-2">
                    <span className="text-teal-600 font-medium">1.</span>
                    <span>{t("results.distribution.empty.step1")}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-teal-600 font-medium">2.</span>
                    <span>{t("results.distribution.empty.step2")}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-teal-600 font-medium">3.</span>
                    <span>{t("results.distribution.empty.step3")}</span>
                  </div>
                </div>
              </div>

              <Button
                onClick={handleOpenModal}
                className="bg-teal-600 hover:bg-teal-700 text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                {t("results.distribution.button.addDistribution")}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Distribution Configuration Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5" />
              {t("results.distribution.modal.title")}
            </DialogTitle>
            <DialogDescription>
              {t("results.distribution.modal.description")}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            {currentStep === 1 && (
              <DistributionProductStep
                onNext={handleStep1Next}
                onClose={handleCloseModal}
                initialProductId={selectedProduct?.id || ""}
                initialFootprintType={selectedFootprintType || undefined}
              />
            )}
            
            {currentStep === 2 && selectedProduct && selectedFootprintType && (
              <>
                {selectedFootprintType === 'carbon' ? (
                  <DistributionCarbonFootprintStep
                    selectedProduct={selectedProduct}
                    onNext={handleStep2Next}
                    onBack={handleStep2Back}
                    onClose={handleCloseModal}
                    initialTransportData={transportData}
                    editingDistribution={editingDistribution ? {
                      id: editingDistribution.id,
                      transport_data: editingDistribution.transport_data
                    } : undefined}
                    isEditing={isEditing}
                  />
                ) : selectedFootprintType === 'hydric' ? (
                  <DistributionHydricFootprintStep
                    selectedProduct={selectedProduct}
                    onNext={handleStep2Next}
                    onBack={handleStep2Back}
                    onClose={handleCloseModal}
                    initialTransportData={transportData}
                    editingDistribution={editingDistribution ? {
                      id: editingDistribution.id,
                      transport_data: editingDistribution.transport_data
                    } : undefined}
                    isEditing={isEditing}
                  />
                ) : (
                  <div className="space-y-4">
                    <div className="text-center py-8">
                      <Truck className="h-12 w-12 mx-auto mb-4 text-teal-400" />
                      <h3 className="text-lg font-medium text-gray-700 mb-2">
                        {t("results.distribution.step2.title")} - {t("results.distribution.step2.hydricFootprint")}
                      </h3>
                      <p className="text-gray-500 mb-4">
                        {t("results.distribution.step2.selectedProduct")}: <strong>{selectedProduct?.name}</strong><br />
                        {t("results.distribution.step2.footprintType")}: <strong>{t("results.distribution.step2.hydricFootprint")}</strong>
                      </p>
                      <p className="text-gray-500">
                        {t("common.comingSoon")}
                      </p>
                    </div>

                    <div className="flex justify-between gap-2 pt-4 border-t">
                      <Button
                        variant="outline"
                        onClick={() => setCurrentStep(1)}
                      >
                        {t("common.back")}
                      </Button>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          onClick={handleCloseModal}
                        >
                          {t("common.cancel")}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
